import React, { useEffect, useRef, useState } from 'react';
import { css } from '@linaria/core';
import { classnames } from '../../utils';
import { brand, theme } from '@terveystalo/design-system-theme';
import { TextButton } from '../TextButton';
const containerStyles = css `
  display: flex;
  position: relative;
`;
const contentStyles = css `
  width: max-content;
  z-index: 1;
  position: absolute;
  background-color: ${brand.popover.color.background};
  color: ${brand.popover.color.content};
  padding: ${theme.spacing[1]} ${theme.spacing['0_75']};
  /* Restrict prettier splitting into multiple lines, which breaks some other plugins */
  /* prettier-ignore */
  box-shadow: ${theme.shadow.offset.card.x} ${theme.shadow.offset.card.y} ${theme.shadow.blur.card} ${brand.box.color
    .shadow};
`;
const contentWrapperStyles = css `
  position: relative;
`;
const contentPositionStyles = {
    'top-right': css `
    bottom: 2rem;
    left: -1.75rem;
  `,
    'top-left': css `
    bottom: 2rem;
    right: calc(100% - 1rem);
  `,
    'bottom-left': css `
    top: calc(100% + 0.75rem);
    right: calc(100% - 1rem);
  `,
    'bottom-right': css `
    top: calc(100% + 0.75rem);
    left: -2rem;
  `,
};
const triangleTopStyles = css `
  position: absolute;
  bottom: 1.5rem;
  z-index: 2;
  right: 0;
  width: 0;
  height: 0;
  border-left: 0.75rem solid transparent;
  border-right: 0.75rem solid transparent;
  border-top: 0.75rem solid ${brand.popover.color.background};
`;
const triangleBottomStyles = css `
  position: absolute;
  top: 100%;
  z-index: 2;
  right: 0;
  width: 0;
  height: 0;
  border-left: 0.75rem solid transparent;
  border-right: 0.75rem solid transparent;
  border-bottom: 0.75rem solid ${brand.popover.color.background};
`;
// Increase the click area for better usability
const triggerButtonStyles = css `
  display: flex;
  flex-shrink: 0;
  padding: 1rem;
  margin: -1rem;
`;
/**
 * Popover component to give additional information for an input component.
 *
 * Accessibility considerations inspired by:
 * - https://sarahmhigley.com/writing/tooltips-in-wcag-21/
 * - https://techservicesillinois.github.io/accessibility/examples/popover.html
 *
 * Keyboard usage:
 * - Focus shows the popover content
 * - Blur hides the popover content
 * - ESC hides the popover content
 *
 * Touch usage:
 * - Pointerup for the trigger shows the popover content
 * - Pointerup outside the popover hides the popover content (unless there's text selected in the popover content)
 *
 * Mouse / pen usage:
 * - Pointerover for the trigger shows the popover content
 * - Pointerup for the trigger shows the popover content
 * - Pointerout for the trigger + content hides the popover content (unless there's text selected in the popover content)
 * - Pointerup outside the popover hides the popover content (unless there's text selected in the popover content)
 *
 */
export const Popover = ({ children, className, label, id, position = 'top-right' }) => {
    const [visible, setVisible] = useState(false);
    const wrapperRef = useRef(null);
    const triggerRef = useRef(null);
    const isTopPosition = position === 'top-right' || position === 'top-left';
    useEffect(() => {
        /**
         * For all input devices hide the popover when the user clicks outside of it.
         * Unless there's text selected in the popover content.
         */
        const onClick = (event) => {
            const isTrigger = triggerRef.current?.contains(event.target);
            const isTextSelected = isTextWithinElementSelected(wrapperRef.current);
            if (!isTrigger && !isTextSelected) {
                setVisible(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                setVisible(false);
            }
        };
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('pointerup', onClick);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('pointerup', onClick);
        };
    }, []);
    return (React.createElement("div", { className: containerStyles, 
        /**
         * On mouse and pen, hide the popover when the user's "hover" leaves the trigger.
         */
        onPointerLeave: (event) => {
            if (event.pointerType === 'touch') {
                return;
            }
            if (!isTextWithinElementSelected(wrapperRef.current)) {
                setVisible(false);
            }
        } },
        React.createElement(TextButton, { iconOnly: true, iconLeft: "Info", ref: triggerRef, "aria-label": label, "aria-expanded": visible, className: triggerButtonStyles, onFocus: () => setVisible(true), onBlur: () => setVisible(false), 
            /**
             * On mouse and pen, show the popover when the user "hovers" the trigger.
             */
            onPointerOver: (event) => {
                if (event.pointerType === 'touch') {
                    return;
                }
                setVisible(true);
            }, 
            /**
             * For all input devices, toggle the popover when the user clicks the trigger.
             */
            onPointerUp: (event) => {
                event.stopPropagation();
                if (!isTextWithinElementSelected(wrapperRef.current)) {
                    setVisible(!visible);
                }
            } }),
        visible && (React.createElement("div", { className: contentWrapperStyles, ref: wrapperRef },
            React.createElement("div", { className: isTopPosition ? triangleTopStyles : triangleBottomStyles, "aria-hidden": true }),
            React.createElement("div", { "aria-hidden": !visible, id: id, className: classnames(contentStyles, contentPositionStyles[position], className) }, children)))));
};
function isTextWithinElementSelected(element) {
    const currentSelection = window.getSelection()?.toString();
    const contentText = element?.innerText.trim();
    if (currentSelection && contentText?.includes(currentSelection)) {
        return true;
    }
    return false;
}


import "../../../css/atoms/Popover/Popover.css";
